'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { scrollIntoViewSettled } from '~/utils/scrollIntoViewSettled';
import styles from './ScrollspyNav.module.css';
import type { ScrollspyNavItem, ScrollspyNavProps } from './ScrollspyNav.types';

const DEFAULT_NAV_OFFSET = 52;

function getActiveSection(items: readonly ScrollspyNavItem[], offsetPx: number): string {
  let active = items[0]?.id ?? '';
  let bestTop = Number.NEGATIVE_INFINITY;

  for (const { id } of items) {
    const el = document.getElementById(id);
    if (!el) continue;
    const top = el.getBoundingClientRect().top;
    if (top <= offsetPx && top > bestTop) {
      bestTop = top;
      active = id;
    }
  }

  return active;
}

export function ScrollspyNav({ items, ariaLabel, offsetPx, className, ...rest }: ScrollspyNavProps) {
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? '');
  const navRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const navOffsetRef = useRef(offsetPx ?? DEFAULT_NAV_OFFSET);
  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollRunRef = useRef(0);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useLayoutEffect(() => {
    if (offsetPx !== undefined) {
      navOffsetRef.current = offsetPx;
      if (!isProgrammaticScrollRef.current) setActiveId(getActiveSection(itemsRef.current, offsetPx));
      return;
    }
    const nav = navRef.current;
    if (!nav) return;
    const ro = new ResizeObserver(([entry]) => {
      const contentHeight = entry.contentRect.height;
      if (contentHeight > 0) {
        navOffsetRef.current = contentHeight;
        if (!isProgrammaticScrollRef.current) setActiveId(getActiveSection(itemsRef.current, contentHeight));
      }
    });
    ro.observe(nav);
    return () => ro.disconnect();
  }, [offsetPx]);

  useEffect(() => {
    document.documentElement.dataset.scrollspyLive = 'true';
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (isProgrammaticScrollRef.current) return;
      setActiveId(getActiveSection(itemsRef.current, navOffsetRef.current));
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [items]);

  useEffect(() => {
    const elements = items
      .map((element) => document.getElementById(element.id))
      .filter((el): el is HTMLElement => el != null);
    if (elements.length === 0) return;

    const rootMarginTop = offsetPx ?? DEFAULT_NAV_OFFSET;
    const observer = new IntersectionObserver(
      () => {
        if (isProgrammaticScrollRef.current) return;
        setActiveId(getActiveSection(itemsRef.current, navOffsetRef.current));
      },
      { root: null, rootMargin: `-${rootMarginTop}px 0px 0px 0px`, threshold: 0 }
    );
    elements.forEach((el) => {
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, [items, offsetPx]);

  const scrollTabIntoView = (tabId: string) => {
    const btn = listRef.current?.querySelector<HTMLElement>(`button[data-tab-id="${tabId}"]`);
    btn?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  const navigateToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (!el) return;

    const run = ++programmaticScrollRunRef.current;
    isProgrammaticScrollRef.current = true;
    setActiveId(sectionId);
    scrollTabIntoView(sectionId);
    void scrollIntoViewSettled(el).finally(() => {
      if (programmaticScrollRunRef.current === run) {
        isProgrammaticScrollRef.current = false;
      }
    });
  };

  const handleListClick = (e: React.MouseEvent<HTMLUListElement>) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('button[data-tab-id]');
    const sectionId = btn?.getAttribute('data-tab-id');
    if (sectionId && items.some((t) => t.id === sectionId)) navigateToSection(sectionId);
  };

  return (
    <nav
      ref={navRef}
      className={className ? `${styles.nav} ${className}` : styles.nav}
      aria-label={ariaLabel}
      {...rest}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: click is delegated from child <button> elements which handle keyboard natively */}
      <ul ref={listRef} className={styles.list} onClick={handleListClick}>
        {items.map(({ id, label }) => (
          <li key={id} className={styles.item}>
            <button
              type="button"
              className={styles.tab}
              aria-current={activeId === id ? 'location' : undefined}
              data-tab-id={id}
              data-test-id={`scrollspy-tab-${id}`}
            >
              {label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
